import type { Reporting } from '@local/common-lib';

export const monthlySummaryColumns: {
  key: keyof Reporting.MonthlySummaryRow;
  label: string;
}[] = [
  { key: 'date', label: 'Date' },
  { key: 'primary', label: 'Primary' },
  { key: 'otherPatrollers', label: 'Other Patrollers' },
  { key: 'guests', label: 'Guests' },
  { key: 'start', label: 'Start' },
  { key: 'finish', label: 'Finish' },
  { key: 'duration', label: 'Duration' },
  { key: 'rego', label: 'Rego' },
  { key: 'odoStart', label: 'Odo Start' },
  { key: 'odoFinish', label: 'Odo Finish' },
  { key: 'totalKms', label: 'Total KMs' },
  { key: 'vehicleRelated', label: 'Vehicle Related' },
  { key: 'propertyRelated', label: 'Property Related' },
  { key: 'damagedProperty', label: 'Damaged Property' },
  { key: 'disorderRelated', label: 'Disorder Related' },
  { key: 'peopleRelated', label: 'People Related' },
  { key: 'specialService', label: 'Special Service' },
  { key: 'other', label: 'Other' },
];

const monthlySummaryCountKeys = new Set<keyof Reporting.MonthlySummaryRow>([
  'damagedProperty',
  'disorderRelated',
  'other',
  'peopleRelated',
  'propertyRelated',
  'specialService',
  'vehicleRelated',
]);

export const monthlySummaryDisplayValue = (
  key: keyof Reporting.MonthlySummaryRow,
  value: string | number | undefined,
): string | number =>
  monthlySummaryCountKeys.has(key) && value === 0 ? '-' : (value ?? '');

const rowsForExport = (
  report: Reporting.MonthlySummary,
): (string | number | undefined)[][] =>
  [...report.rows, report.totals].map((row) =>
    monthlySummaryColumns.map(({ key }) => row[key]),
  );

const safeCell = (value: string | number | undefined): string => {
  const text = value === undefined ? '' : String(value);
  const formulaSafe = /^[=+\-@]/.test(text) ? `'${text}` : text;
  return /[",\n\r]/.test(formulaSafe)
    ? `"${formulaSafe.replaceAll('"', '""')}"`
    : formulaSafe;
};

export const monthlySummaryCsv = (report: Reporting.MonthlySummary): string =>
  [
    monthlySummaryColumns.map(({ label }) => safeCell(label)),
    ...rowsForExport(report).map((row) => row.map(safeCell)),
  ]
    .map((row) => row.join(','))
    .join('\r\n');

export const downloadMonthlySummaryCsv = (
  report: Reporting.MonthlySummary,
  month: string,
): void => {
  const blob = new Blob([`\ufeff${monthlySummaryCsv(report)}`], {
    type: 'text/csv;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `monthly-summary-${month}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

export const downloadMonthlySummaryXlsx = (
  report: Reporting.MonthlySummary,
  month: string,
): void => {
  const workbook = xlsxWorkbook(
    [monthlySummaryColumns.map(({ label }) => label), ...rowsForExport(report)],
    'Monthly Summary',
  );
  const blob = new Blob([workbook], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `monthly-summary-${month}.xlsx`;
  link.click();
  URL.revokeObjectURL(url);
};

const xml = (value: string | number | undefined): string =>
  String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&apos;');

const crc32 = (input: Uint8Array): number => {
  let crc = 0xffffffff;
  for (const byte of input) {
    crc ^= byte;
    for (let bit = 0; bit < 8; bit += 1) {
      crc = (crc >>> 1) ^ (crc & 1 ? 0xedb88320 : 0);
    }
  }
  return (crc ^ 0xffffffff) >>> 0;
};

const uint16 = (value: number): Uint8Array =>
  new Uint8Array([value & 0xff, (value >>> 8) & 0xff]);
const uint32 = (value: number): Uint8Array =>
  new Uint8Array([
    value & 0xff,
    (value >>> 8) & 0xff,
    (value >>> 16) & 0xff,
    (value >>> 24) & 0xff,
  ]);
const joinBytes = (parts: Uint8Array[]): Uint8Array => {
  const output = new Uint8Array(
    parts.reduce((total, part) => total + part.length, 0),
  );
  let offset = 0;
  for (const part of parts) {
    output.set(part, offset);
    offset += part.length;
  }
  return output;
};

export const xlsxWorkbook = (
  rows: (string | number | undefined)[][],
  sheetName: string,
): Uint8Array => {
  const encoder = new TextEncoder();
  const worksheetRows = rows
    .map(
      (row, rowIndex) =>
        `<row r="${rowIndex + 1}">${row
          .map((cell, columnIndex) => {
            const column = String.fromCharCode(65 + columnIndex);
            return typeof cell === 'number'
              ? `<c r="${column}${rowIndex + 1}"><v>${cell}</v></c>`
              : `<c r="${column}${rowIndex + 1}" t="inlineStr"><is><t>${xml(cell)}</t></is></c>`;
          })
          .join('')}</row>`,
    )
    .join('');
  const sourceFiles: [string, string][] = [
    [
      '[Content_Types].xml',
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/></Types>',
    ],
    [
      '_rels/.rels',
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>',
    ],
    [
      'xl/workbook.xml',
      `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets><sheet name="${xml(sheetName)}" sheetId="1" r:id="rId1"/></sheets></workbook>`,
    ],
    [
      'xl/_rels/workbook.xml.rels',
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/></Relationships>',
    ],
    [
      'xl/worksheets/sheet1.xml',
      `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><sheetData>${worksheetRows}</sheetData></worksheet>`,
    ],
  ];
  const files = sourceFiles.map(([name, contents]) => ({
    contents: encoder.encode(contents),
    name: encoder.encode(name),
  }));
  let offset = 0;
  const entries: Uint8Array[] = [];
  const centralDirectory: Uint8Array[] = [];
  for (const file of files) {
    const crc = crc32(file.contents);
    entries.push(
      joinBytes([
        uint32(0x04034b50),
        uint16(20),
        uint16(0),
        uint16(0),
        uint16(0),
        uint16(0),
        uint32(crc),
        uint32(file.contents.length),
        uint32(file.contents.length),
        uint16(file.name.length),
        uint16(0),
        file.name,
        file.contents,
      ]),
    );
    centralDirectory.push(
      joinBytes([
        uint32(0x02014b50),
        uint16(20),
        uint16(20),
        uint16(0),
        uint16(0),
        uint16(0),
        uint16(0),
        uint32(crc),
        uint32(file.contents.length),
        uint32(file.contents.length),
        uint16(file.name.length),
        uint16(0),
        uint16(0),
        uint16(0),
        uint16(0),
        uint32(0),
        uint32(offset),
        file.name,
      ]),
    );
    offset += entries[entries.length - 1]?.length ?? 0;
  }
  const directory = joinBytes(centralDirectory);
  return joinBytes([
    ...entries,
    directory,
    uint32(0x06054b50),
    uint16(0),
    uint16(0),
    uint16(files.length),
    uint16(files.length),
    uint32(directory.length),
    uint32(offset),
    uint16(0),
  ]);
};
